// @ts-nocheck
import React, { useState, useEffect } from 'react';
import Overview from '../../../containers/Overview';
import { fetchMonthly } from '../../../utils/fetch';
import Layout from '../../../components/Layout';
import Loading from '../../../components/Loading';
import { genYYYYMM, countTotalWeekdayInMonths } from '../../../utils';
import TemplateLayout from '../../../components/LayoutViz';
import {
    getSystemList,
    getConfig,
    getAvailableAnalysis,
    getSystemListWithConfigAndGeo,
} from '../../../utils/getPageProps';
import { OVERVIEW_PAGE, YEARLY_REPORT } from '../../../utils/static';
import { S3DataType, ShareProps } from '../../../../types/share';
import {getDynamicPageLocalePath} from '../../../utils/staticPath';

type Props = {
    yyyymm: ShareProps['yyyymm'],
    system: ShareProps['system']
    year: ShareProps['year'],
    month: ShareProps['month'],
    locale: ShareProps['locale'],
    config: ShareProps['config'],
    page: ShareProps['page'],
    systemList: ShareProps['systemList'],
    availableAnalysis: ShareProps['availableAnalysis'],
    numOfWeekDayInThisDateRange: ShareProps['numOfWeekDayInThisDateRange'],
    seo: ShareProps['seo'],
}

const OverviewTemplate = ({
    yyyymm,
    systemList,
    locale,
    system,
    year,
    month,
    availableAnalysis,
    numOfWeekDayInThisDateRange,
    page,
    config,
    seo,
}: Props) => {
    const [geo, setGeo] = useState<S3DataType['geo']>(null);
    const [isMountLoaded, setIsMountLoaded] = useState(false);
    const [stationDeal, setStationDeal] = useState<S3DataType['stationDeal']>(null);
    const [dateDeal, setDateDeal] = useState<S3DataType['dateDeal']>(null);
    const [totalWdayDeal, setWdayDeal] = useState<S3DataType['totalWdayDeal']>(null);
    const [timeDeal, setTimeDeal] = useState<S3DataType['timeDeal']>(null);
    const [movingTimeWday, setMovingTimeWday] = useState<S3DataType['movingTimeWday']>(null);
    const [movingTimeRange, setMovingTimeRange] = useState<S3DataType['movingTimeRange']>(null);
    const [routeRank, setRouteRank] = useState<S3DataType['routeRank']>(null);
    const [hourlyDeal, setHourlyDeal] = useState<S3DataType['hourlyDeal']>(null);

    // Mount action
    useEffect(() => {
        setIsMountLoaded(false);
        Promise.all([
            fetchMonthly({ yyyymm, system, filename: 'station_deal.json' }),
            fetchMonthly({ yyyymm, system, filename: 'date_deal.json' }),
            fetchMonthly({ yyyymm, system, filename: 'total_wday_deal.json' }),
            fetchMonthly({ yyyymm, system, filename: 'time_deal.json' }),
            fetchMonthly({ yyyymm, system, filename: 'moving_time_wday.json' }),
            fetchMonthly({ yyyymm, system, filename: 'route_rank.json' }),
            fetchMonthly({ yyyymm, system, filename: 'hourly_deal.json' }),
            fetchMonthly({ yyyymm, system, filename: 'moving_time_range.json' }),
            fetchMonthly({ yyyymm, system, filename: 'geo.json' }),
        ]).then((results) => {
            const [
                _stationDeal,
                _dateDeal,
                _totalWdayDeal,
                _timeDeal,
                _movingTimeWday,
                _routeRank,
                _hourlyDeal,
                _movingTimeRange,
                _geo,
            ] = results;
            setStationDeal(_stationDeal);
            setDateDeal(_dateDeal);
            setWdayDeal(_totalWdayDeal);
            setTimeDeal(_timeDeal);
            setMovingTimeWday(_movingTimeWday);
            setRouteRank(_routeRank);
            setHourlyDeal(_hourlyDeal);
            setMovingTimeRange(_movingTimeRange);
            setGeo(_geo);
            setIsMountLoaded(true);
        });
    }, [yyyymm]);
    return (
        <Layout seo={seo} locale={locale}>
            <TemplateLayout
                page={page}
                system={system}
                locale={locale}
                config={config}
                year={year}
                month={month}
                systemList={systemList}
                availableAnalysis={availableAnalysis}
            >
                {isMountLoaded ? (
                    <Overview
                        reportType={config.reportType}
                        numOfWeekDayInThisDateRange={numOfWeekDayInThisDateRange}
                        yyyymm={yyyymm}
                        config={config}
                        locale={locale}
                        stationDeal={stationDeal}
                        dateDeal={dateDeal}
                        totalWdayDeal={totalWdayDeal}
                        timeDeal={timeDeal}
                        movingTimeWday={movingTimeWday}
                        geo={geo}
                        routeRank={routeRank}
                        hourlyDeal={hourlyDeal}
                        sortedDateRange={config.dateRange}
                        movingTimeRange={movingTimeRange}
                        year={year}
                        month={month}
                    />
                ) : <Loading />}
            </TemplateLayout>
        </Layout>
    );
};

export async function getStaticProps(context) {
    const { locale } = context;
    const { date, system } = context.params;
    const year = date[0];
    const month = date[1] || null;
    const yyyymm = genYYYYMM(year, month);

    const config = getConfig({
        system, year, month, locale, page: OVERVIEW_PAGE,
    });
    return {
        props: {
            yyyymm,
            systemList: getSystemList(locale),
            locale,
            system,
            year,
            month: month || null,
            availableAnalysis: getAvailableAnalysis(system),
            numOfWeekDayInThisDateRange: countTotalWeekdayInMonths(config.dateRange),
            page: OVERVIEW_PAGE,
            config,
            seo: {
                page: OVERVIEW_PAGE,
                year,
                month,
                system,
                isYearlyReport: config.reportType === YEARLY_REPORT
            }
        },
    };
}

export async function getStaticPaths() {
    let paths = [];
    getSystemListWithConfigAndGeo().forEach(({
        system, year, month, reportType,
    }) => {
        if (reportType === YEARLY_REPORT) {
            paths = [
                ...paths,
                ...getDynamicPageLocalePath(
                    {
                        system,
                        date: [`${year}`],
                    }
                )
            ];
        } else {
            paths = [
                ...paths,
                ...getDynamicPageLocalePath(
                    {
                        system,
                        date: [`${year}`, `${month}`],
                    }
                )
            ];
        }
    });

    return {
        paths,
        fallback: false, // See the "fallback" section below
    };
}

export default OverviewTemplate;
